import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Rocket, ArrowLeft, FileText, Shield, Cookie, KeyRound, Lock, Users } from 'lucide-react';

type LegalTab = 'terms' | 'privacy' | 'cookies' | 'licensing' | 'compliance' | 'affiliate';

const tabs: { id: LegalTab; label: string; icon: React.ElementType }[] = [
  { id: 'terms', label: 'Terms of Service', icon: FileText },
  { id: 'privacy', label: 'Privacy Policy', icon: Shield },
  { id: 'cookies', label: 'Cookie Policy', icon: Cookie },
  { id: 'licensing', label: 'Licensing Agreement', icon: KeyRound },
  { id: 'compliance', label: 'GDPR Compliance', icon: Lock },
  { id: 'affiliate', label: 'Affiliate Terms', icon: Users },
];

const validTabs: LegalTab[] = ['terms', 'privacy', 'cookies', 'licensing', 'compliance', 'affiliate'];

const LegalHub: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<LegalTab>(() => {
    const pathSegment = location.pathname.split('/').pop() ?? '';
    if (tab && validTabs.includes(tab as LegalTab)) return tab as LegalTab;
    if (validTabs.includes(pathSegment as LegalTab)) return pathSegment as LegalTab;
    return 'terms';
  });

  useEffect(() => {
    if (tab && validTabs.includes(tab as LegalTab)) {
      setActiveTab(tab as LegalTab);
    } else {
      const pathSegment = location.pathname.split('/').pop() ?? '';
      if (validTabs.includes(pathSegment as LegalTab)) {
        setActiveTab(pathSegment as LegalTab);
      }
    }
  }, [tab, location.pathname]);

  const lastUpdated = 'September 14, 2026';

  const renderContent = () => {
    switch (activeTab) {
      case 'terms':
        return <TermsOfService updated={lastUpdated} />;
      case 'privacy':
        return <PrivacyPolicy updated={lastUpdated} />;
      case 'cookies':
        return <CookiePolicy updated={lastUpdated} />;
      case 'licensing':
        return <LicensingAgreement updated={lastUpdated} />;
      case 'compliance':
        return <GDPRCompliance updated={lastUpdated} />;
      case 'affiliate':
        return <AffiliateTerms updated={lastUpdated} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Showcase
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-brand rounded-lg flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-800">Aurelian Marketing Engine</span>
          </div>
        </div>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Legal Documents</h1>
          <p className="text-sm text-slate-400">
            The policies and agreements that govern your use of the Aurelian Marketing Engine platform.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-200 pb-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all ${
                activeTab === t.id
                  ? 'bg-white/5 text-primary-600 border-b-2 border-primary-500'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            >
              <t.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="glass-card p-6 md:p-10 animate-fade-in">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-300 font-mono">
            &copy; {new Date().getFullYear()} Aurelian Marketing Engine. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className="text-[10px] text-slate-300 hover:text-primary-600 transition-colors font-mono"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared section components
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-base font-bold text-slate-800 mb-2">{title}</h2>
    <div className="text-sm text-slate-500 leading-relaxed space-y-2">{children}</div>
  </div>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p>{children}</p>
);

const List: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc list-outside ml-5 space-y-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const UpdatedBadge: React.FC<{ date: string }> = ({ date }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 border border-primary-200 mb-6">
    <span className="text-[10px] font-mono text-primary-600 uppercase tracking-wider">Last Updated: {date}</span>
  </div>
);

// === Terms of Service ===
const TermsOfService: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">Terms of Service</h1>

    <Section title="1. Acceptance of Terms">
      <Para>By accessing or using the Aurelian Marketing Engine ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Platform.</Para>
      <Para>The Platform is provided by Aurelian Marketing Engine ("we," "us," or "our"), a content marketing automation service. These Terms constitute a legally binding agreement between you and us.</Para>
    </Section>

    <Section title="2. Description of Service">
      <Para>The Platform provides AI-powered content marketing tools including but not limited to:</Para>
      <List items={[
        'Research and keyword analysis tools',
        'AI-powered content generation across multiple formats',
        'Content library management and organization',
        'Multi-channel publishing and deployment',
        'Email marketing and auto-responder sequences',
        'Affiliate link tracking and analytics',
        'Social media scheduling and management',
        'Image generation and cover page design',
        'RSS feed creation and management',
        'Analytics and performance tracking',
        'Live chat and customer engagement tools',
      ]} />
    </Section>

    <Section title="3. Account Registration">
      <Para>To access most features of the Platform, you must register for an account. You agree to:</Para>
      <List items={[
        'Provide accurate, current, and complete information during registration',
        'Maintain the security of your password and account credentials',
        'Notify us immediately of any unauthorized use of your account',
        'Be responsible for all activities that occur under your account',
      ]} />
    </Section>

    <Section title="4. Acceptable Use">
      <Para>You agree not to use the Platform to:</Para>
      <List items={[
        'Generate, store, or distribute content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable',
        'Infringe upon the intellectual property rights, privacy, or other rights of any third party',
        'Upload or transmit viruses, malware, or any other malicious code',
        'Attempt to gain unauthorized access to any part of the Platform, other accounts, or computer systems or networks connected to the Platform',
        'Use the Platform for any illegal, fraudulent, or unauthorized purpose',
        'Reverse engineer, decompile, or disassemble any portion of the Platform',
        'Use automated systems (bots, scrapers, crawlers) to access the Platform without our express written permission',
      ]} />
    </Section>

    <Section title="5. Content Ownership and Licensing">
      <Para>You retain all ownership rights to content you create, upload, or generate using the Platform. By using the Platform, you grant us a limited, non-exclusive license to process your content solely for the purpose of providing and improving the Platform's services.</Para>
      <Para>Content generated by the Platform's AI tools is owned by you, subject to any applicable third-party rights. You are responsible for ensuring that generated content complies with all applicable laws and does not infringe on third-party rights.</Para>
    </Section>

    <Section title="6. AI-Generated Content Disclaimer">
      <Para>The Platform uses artificial intelligence to generate content. While we strive for accuracy and quality, AI-generated content may contain errors, inaccuracies, or biased outputs. You are solely responsible for reviewing, editing, and approving all AI-generated content before publishing or distributing it.</Para>
      <Para>We do not guarantee the accuracy, completeness, or suitability of AI-generated content for any specific purpose. You should always review generated content for factual accuracy, legal compliance, and brand alignment.</Para>
    </Section>

    <Section title="7. Subscription and Payment">
      <Para>The Platform may offer subscription plans with varying features and limitations. Subscription fees are billed in advance on a recurring basis (monthly or annually) unless otherwise specified.</Para>
      <List items={[
        'All fees are non-refundable except where required by law',
        'You may cancel your subscription at any time; cancellation takes effect at the end of the current billing period',
        'We may change subscription fees with at least 30 days notice',
        'Taxes, where applicable, are added to subscription fees',
      ]} />
    </Section>

    <Section title="8. Service Availability">
      <Para>We strive to maintain high availability but do not guarantee uninterrupted access to the Platform. We are not liable for any downtime, service interruptions, or data loss caused by:</Para>
      <List items={[
        'Scheduled maintenance or updates',
        'Unforeseen technical issues or infrastructure failures',
        'Third-party service outages (e.g., cloud providers, API services)',
        'Force majeure events including natural disasters, strikes, or governmental actions',
      ]} />
    </Section>

    <Section title="9. Termination">
      <Para>We may suspend or terminate your access to the Platform at any time, with or without cause, and with or without notice. Upon termination, all of your data may be permanently deleted from our systems within 30 days, except where retention is required by law.</Para>
      <Para>You may terminate your account at any time through the Platform's settings or by contacting us. Termination does not relieve you of any obligations incurred prior to termination.</Para>
    </Section>

    <Section title="10. Disclaimers and Limitation of Liability">
      <Para>The Platform is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not warrant that the Platform will be error-free, secure, or available at all times.</Para>
      <Para>To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill, arising from your use of or inability to use the Platform.</Para>
      <Para>Our total aggregate liability for all claims arising from these Terms or your use of the Platform shall not exceed the amount you paid us in the 12 months preceding the claim.</Para>
    </Section>

    <Section title="11. Governing Law">
      <Para>These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. Any disputes arising from these Terms shall be resolved through binding arbitration, with the venue determined by our registered address.</Para>
    </Section>

    <Section title="12. Changes to These Terms">
      <Para>We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on this page and, where appropriate, sending a notification through the Platform. Your continued use of the Platform after changes take effect constitutes acceptance of the updated Terms.</Para>
    </Section>

    <Section title="13. Affiliate Program">
      <Para>The Platform offers an affiliate program that allows registered users to earn recurring commission on referrals that convert to paid subscriptions. The following terms apply to the affiliate program:</Para>
      <List items={[
        'Commission rates are tiered by the referred customer subscription plan: Starter 20%, Pro 25%, and Agency/Enterprise 30% for the first 12 months of the referred customer subscription',
        'After the first 12 months, commission rates step down to long-term rates: Starter 10%, Pro 12.5%, and Agency/Enterprise 15%',
        'Commission is calculated as a percentage of the referred customer subscription fee and is paid on each successful recurring billing cycle for as long as the referred customer remains an active paying subscriber',
        'A minimum payout balance of A$50 is required before a withdrawal can be requested',
        'We reserve the right to modify commission rates, tiers, or payout thresholds at any time with at least 30 days notice to existing affiliates',
        'Affiliate commissions are not payable on referrals that result in refunds, chargebacks, or fraudulent transactions',
        'Self-referrals are not permitted; you may not earn commission on your own subscription',
        'We may suspend or terminate an affiliate account at any time for violation of these Terms or fraudulent referral activity',
      ]} />
      <Para>Commission rates and program terms are subject to change. The rates in effect at the time a referral converts will apply to that referral for its initial 12-month period, after which the long-term rates then in effect will apply.</Para>
    </Section>

    <Section title="14. Contact">
      <Para>For questions about these Terms, please contact us at legal@aurelianmarketingengine.com.</Para>
    </Section>
  </div>
);

// === Privacy Policy ===
const PrivacyPolicy: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">Privacy Policy</h1>

    <Section title="1. Introduction">
      <Para>This Privacy Policy explains how Aurelian Marketing Engine ("we," "us") collects, uses, discloses, and safeguards your information when you use our Platform. We are committed to protecting your privacy and complying with applicable data protection laws.</Para>
    </Section>

    <Section title="2. Information We Collect">
      <Para>We collect the following types of information:</Para>
      <Para><strong className="text-slate-600">Account Information:</strong> Email address, password (encrypted), and account preferences.</Para>
      <Para><strong className="text-slate-600">Profile Information:</strong> Name, company name, brand colors, and other details you provide during setup.</Para>
      <Para><strong className="text-slate-600">Content Data:</strong> Research briefs, generated content, email sequences, affiliate links, scheduled posts, images, and other assets you create.</Para>
      <Para><strong className="text-slate-600">Usage Data:</strong> Pages visited, features used, time spent, click events, and interaction data collected via analytics events.</Para>
      <Para><strong className="text-slate-600">Technical Data:</strong> IP address, browser type, device information, and operating system details.</Para>
      <Para><strong className="text-slate-600">API Keys:</strong> Third-party API keys you store in the encrypted vault (stored encrypted, never displayed in plaintext).</Para>
    </Section>

    <Section title="3. How We Use Your Information">
      <Para>We use your information to:</Para>
      <List items={[
        'Provide, operate, and maintain the Platform and its features',
        'Process content generation requests and deliver AI-powered outputs',
        'Authenticate your identity and manage your account',
        'Send service-related notifications, updates, and security alerts',
        'Analyze usage patterns to improve Platform performance and features',
        'Track analytics events to provide you with performance insights',
        'Comply with legal obligations and enforce our Terms of Service',
        'Prevent fraud, abuse, and unauthorized access',
      ]} />
    </Section>

    <Section title="4. Data Storage and Security">
      <Para>Your data is stored on secure, encrypted databases hosted by Supabase (PostgreSQL). We employ the following security measures:</Para>
      <List items={[
        'Row-level security (RLS) on every database table, ensuring users can only access their own data',
        'Encryption at rest for all stored data',
        'Encrypted storage for API keys and sensitive credentials',
        'Secure authentication via Supabase Auth with bcrypt password hashing',
        'HTTPS/TLS encryption for all data in transit',
        'Regular security audits and vulnerability assessments',
      ]} />
    </Section>

    <Section title="5. Data Sharing and Disclosure">
      <Para>We do not sell your personal information. We may share your data with:</Para>
      <List items={[
        'Cloud infrastructure providers (Supabase) who host and process your data',
        'AI service providers (OpenAI, Anthropic, Google) who process content generation requests — your prompts and generated content are sent to these services to produce results',
        'Analytics providers who help us understand Platform usage',
        'Legal authorities when required by law, court order, or government regulation',
      ]} />
      <Para>When sharing data with AI providers, only the content necessary to fulfill your request is transmitted. We do not share your account credentials, payment information, or personal data with AI providers.</Para>
    </Section>

    <Section title="6. Data Retention">
      <Para>We retain your data for as long as your account is active. When you delete your account, we permanently remove your data from our systems within 30 days, except:</Para>
      <List items={[
        'Data we are legally required to retain (e.g., financial records)',
        'Anonymized, aggregated data that cannot be linked to your identity',
        'Backup copies that are overwritten on a regular schedule',
      ]} />
    </Section>

    <Section title="7. Your Privacy Rights">
      <Para>Depending on your jurisdiction, you may have the following rights:</Para>
      <List items={[
        'Access: Request a copy of your personal data',
        'Rectification: Request correction of inaccurate data',
        'Erasure: Request deletion of your personal data ("right to be forgotten")',
        'Restriction: Request that we limit processing of your data',
        'Portability: Request your data in a structured, machine-readable format',
        'Objection: Object to certain types of processing',
        'Withdrawal: Withdraw consent to data processing at any time',
      ]} />
      <Para>To exercise these rights, contact us at privacy@aurelianmarketingengine.com.</Para>
    </Section>

    <Section title="8. Cookies and Tracking">
      <Para>The Platform uses minimal cookies and local storage for authentication and session management. We do not use cookies for advertising tracking. See our Cookie Policy for details.</Para>
    </Section>

    <Section title="9. Children's Privacy">
      <Para>The Platform is not intended for individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</Para>
    </Section>

    <Section title="10. International Data Transfer">
      <Para>Your data may be processed and stored in countries other than your own, including the United States and European Union. We ensure appropriate safeguards are in place for international data transfers, including Standard Contractual Clauses where applicable.</Para>
    </Section>

    <Section title="11. Changes to This Policy">
      <Para>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page. Your continued use of the Platform after changes take effect constitutes acceptance of the updated policy.</Para>
    </Section>

    <Section title="12. Contact">
      <Para>For privacy questions or requests, contact us at privacy@aurelianmarketingengine.com.</Para>
    </Section>
  </div>
);

// === Cookie Policy ===
const CookiePolicy: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">Cookie Policy</h1>

    <Section title="1. What Are Cookies">
      <Para>Cookies are small text files stored on your device when you visit a website. They are widely used to make websites work efficiently and to provide information to site owners. The Aurelian Marketing Engine Platform uses cookies and similar technologies (local storage, session storage) for essential functionality.</Para>
    </Section>

    <Section title="2. Types of Cookies We Use">
      <Para><strong className="text-slate-600">Essential Cookies (Required):</strong></Para>
      <List items={[
        'Authentication cookies: Maintain your login session across page loads',
        'Security tokens: Prevent cross-site request forgery (CSRF) attacks',
        'Session identifiers: Track your current browsing session for Platform functionality',
      ]} />
      <Para><strong className="text-slate-600">Functional Cookies:</strong></Para>
      <List items={[
        'Theme preferences: Store your dark/light mode selection',
        'Layout preferences: Remember sidebar state and display settings',
        'Language settings: Store your preferred language if multiple are available',
      ]} />
      <Para><strong className="text-slate-600">Analytics Cookies (Optional):</strong></Para>
      <List items={[
        'Usage tracking: Count page views, feature usage, and session duration',
        'Error tracking: Help us identify and fix technical issues',
      ]} />
    </Section>

    <Section title="3. Cookies We Do NOT Use">
      <Para>We do not use the following types of cookies:</Para>
      <List items={[
        'Advertising or marketing cookies',
        'Third-party tracking pixels (Facebook Pixel, Google Ads, etc.)',
        'Cross-site tracking cookies for behavioral advertising',
        'Social media tracking cookies',
      ]} />
    </Section>

    <Section title="4. How Long Cookies Persist">
      <Para>Cookie durations on our Platform:</Para>
      <List items={[
        'Session cookies: Deleted when you close your browser',
        'Authentication token: Persists for up to 7 days, then requires re-login',
        'Preferences: Persist for up to 1 year unless you clear browser data',
        'Analytics data: Aggregated and anonymized within 30 days',
      ]} />
    </Section>

    <Section title="5. Managing Cookies">
      <Para>You can control and delete cookies through your browser settings. Most browsers allow you to:</Para>
      <List items={[
        'View all cookies currently stored on your device',
        'Block cookies from specific sites or all sites',
        'Delete cookies when you close your browser',
        'Block third-party cookies entirely',
      ]} />
      <Para>Disabling essential cookies will prevent you from logging in and using the Platform. Disabling analytics cookies will not affect Platform functionality.</Para>
    </Section>

    <Section title="6. Third-Party Services">
      <Para>The Platform integrates with third-party services that may set their own cookies:</Para>
      <List items={[
        'Supabase: Authentication and session management',
        'AI providers (OpenAI, Anthropic): No cookies set — API calls only',
        'Cloud infrastructure: Load balancing and security',
      ]} />
      <Para>We recommend reviewing the cookie policies of these third-party services on their respective websites.</Para>
    </Section>

    <Section title="7. Changes to This Policy">
      <Para>If we change our cookie usage, we will update this policy and notify you through the Platform. We will always obtain your consent before introducing non-essential cookies.</Para>
    </Section>

    <Section title="8. Contact">
      <Para>For questions about cookies, contact us at privacy@aurelianmarketingengine.com.</Para>
    </Section>
  </div>
);

// === Licensing Agreement ===
const LicensingAgreement: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">Licensing Agreement</h1>

    <Section title="1. Software License">
      <Para>The Aurelian Marketing Engine Platform is licensed, not sold. This Licensing Agreement ("Agreement") governs your use of the Platform software, source code, and associated services.</Para>
      <Para>The Platform is released under the MIT License for open-source components. Proprietary components, including the hosted service, AI integrations, and cloud infrastructure, are governed by the terms below.</Para>
    </Section>

    <Section title="2. Open-Source License (MIT)">
      <Para>Portions of the Platform are licensed under the MIT License. Under the MIT License, you are free to:</Para>
      <List items={[
        'Use, copy, modify, merge, publish, distribute, and sublicense the open-source code',
        'Use the code for commercial purposes',
        'Modify and create derivative works',
      ]} />
      <Para>The MIT License is provided "as is" without warranty of any kind. The copyright notice and permission notice must be included in all copies or substantial portions of the software.</Para>
    </Section>

    <Section title="3. Proprietary License">
      <Para>The following components are proprietary and licensed under restricted terms:</Para>
      <List items={[
        'The hosted Platform service and its cloud infrastructure',
        'AI integration pipelines and prompt engineering templates',
        'Brand assets, logos, and visual design elements',
        'Documentation, help content, and training materials',
      ]} />
      <Para>For proprietary components, you are granted a limited, non-exclusive, non-transferable, revocable license to use the Platform through a subscription, subject to your compliance with these terms and our Terms of Service.</Para>
    </Section>

    <Section title="4. Content License">
      <Para>Content you generate using the Platform is licensed as follows:</Para>
      <Para><strong className="text-slate-600">Your Content:</strong> You retain full ownership and copyright of all content you create, upload, or generate. You may use generated content for any lawful purpose, including commercial use.</Para>
      <Para><strong className="text-slate-600">AI-Generated Content:</strong> Content produced by the Platform's AI tools is owned by you, subject to the terms of the underlying AI service providers. Some providers may retain certain rights to generated content as specified in their terms of service.</Para>
      <Para><strong className="text-slate-600">Platform License to Your Content:</strong> You grant us a limited license to process, store, and transmit your content solely for the purpose of providing and operating the Platform. We do not claim ownership of your content.</Para>
    </Section>

    <Section title="5. Permitted Uses">
      <Para>Under your subscription, you may:</Para>
      <List items={[
        'Use the Platform for your own content marketing and business purposes',
        'Generate unlimited content within your plan limits',
        'Publish generated content to your own channels and platforms',
        'Export and download your content at any time',
        'Use the Platform for client work if you are an agency or consultant',
      ]} />
    </Section>

    <Section title="6. Prohibited Uses">
      <Para>You may not:</Para>
      <List items={[
        'Resell, sublicense, or white-label the Platform without written permission',
        'Use the Platform to create competing content marketing services',
        'Remove or alter copyright notices, trademarks, or proprietary markings',
        'Reverse engineer or attempt to extract the Platform source code',
        'Use the Platform to generate content for illegal or fraudulent purposes',
        'Share your account credentials with multiple users (unless on a team plan)',
      ]} />
    </Section>

    <Section title="7. Third-Party Licenses">
      <Para>The Platform incorporates open-source libraries and third-party services, each with their own licenses:</Para>
      <List items={[
        'React (MIT License) — Facebook/Meta',
        'Vite (MIT License) — Evan You',
        'Tailwind CSS (MIT License) — Tailwind Labs',
        'Supabase (Apache 2.0) — Supabase Inc.',
        'Lucide Icons (ISC License) — Lucide Contributors',
      ]} />
      <Para>AI services (OpenAI, Anthropic, Google) are governed by their respective terms of service and usage policies.</Para>
    </Section>

    <Section title="8. Intellectual Property">
      <Para>The Platform, including its design, architecture, code structure, and visual identity, is protected by intellectual property laws. We retain all rights, title, and interest in and to the Platform, except for the open-source components licensed under the MIT License.</Para>
    </Section>

    <Section title="9. Termination of License">
      <Para>Your license to use the Platform terminates automatically if you violate these terms. Upon termination, you must cease using the Platform and destroy all copies of proprietary materials in your possession. Your rights under the MIT License for open-source components survive termination.</Para>
    </Section>

    <Section title="10. Contact">
      <Para>For licensing inquiries, contact us at legal@aurelianmarketingengine.com.</Para>
    </Section>
  </div>
);

// === GDPR Compliance ===
const GDPRCompliance: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">GDPR Compliance</h1>

    <Section title="1. Our Commitment to GDPR">
      <Para>The Aurelian Marketing Engine is committed to full compliance with the General Data Protection Regulation (EU) 2016/679 ("GDPR"). This document explains how we meet our obligations as a data controller and processor under the GDPR.</Para>
    </Section>

    <Section title="2. Lawful Basis for Processing">
      <Para>We process personal data under the following lawful bases:</Para>
      <List items={[
        'Performance of a contract: Processing necessary to provide the Platform services you requested',
        'Legitimate interests: Processing for service improvement, security, and fraud prevention',
        'Consent: Processing of optional data such as analytics and marketing communications',
        'Legal obligation: Processing required to comply with applicable laws and regulations',
      ]} />
    </Section>

    <Section title="3. Data Subject Rights">
      <Para>Under the GDPR, data subjects have the following rights, which we facilitate:</Para>
      <List items={[
        'Right to be informed (Articles 13-14): Clear information about data collection and use',
        'Right of access (Article 15): Obtain a copy of your personal data',
        'Right to rectification (Article 16): Correct inaccurate or incomplete data',
        'Right to erasure (Article 17): Request deletion of your personal data',
        'Right to restrict processing (Article 18): Limit how we process your data',
        'Right to data portability (Article 20): Receive your data in a structured, machine-readable format',
        'Right to object (Article 21): Object to processing based on legitimate interests',
        'Rights related to automated decision-making (Article 22): Not subject to solely automated decisions with significant effects',
      ]} />
      <Para>To exercise any of these rights, contact us at privacy@aurelianmarketingengine.com. We respond to all requests within 30 days.</Para>
    </Section>

    <Section title="4. Data Minimization">
      <Para>We follow the principle of data minimization, collecting only the personal data necessary for the specified purposes:</Para>
      <List items={[
        'Account creation requires only an email address and password',
        'Profile information is optional and collected during the setup wizard',
        'Usage data is limited to functional analytics needed to operate the Platform',
        'We do not collect sensitive special category data (Article 9)',
      ]} />
    </Section>

    <Section title="5. Purpose Limitation">
      <Para>Personal data is processed only for the purposes for which it was collected. We do not repurpose data for unrelated activities without obtaining fresh consent. If we need to use your data for a new purpose, we will notify you and seek consent where required.</Para>
    </Section>

    <Section title="6. Storage Limitation">
      <Para>We retain personal data only as long as necessary for the purposes for which it was collected:</Para>
      <List items={[
        'Active accounts: Data is retained for the duration of your subscription',
        'Closed accounts: Data is permanently deleted within 30 days of account closure',
        'Analytics data: Aggregated and anonymized within 30 days',
        'Legal holds: Data may be retained longer if required by law',
      ]} />
    </Section>

    <Section title="7. Integrity and Confidentiality">
      <Para>We implement appropriate technical and organizational measures to ensure data security:</Para>
      <List items={[
        'Row-level security (RLS) on all database tables — users can only access their own data',
        'Encryption at rest and in transit (AES-256, TLS 1.3)',
        'Encrypted vault for storing third-party API keys',
        'Access controls and authentication for all internal systems',
        'Regular security assessments and penetration testing',
        'Staff training on data protection and privacy',
        'Incident response plan with 72-hour breach notification capability',
      ]} />
    </Section>

    <Section title="8. International Data Transfers">
      <Para>Personal data may be transferred to and processed in countries outside the European Economic Area (EEA). For such transfers, we ensure appropriate safeguards are in place:</Para>
      <List items={[
        'Standard Contractual Clauses (SCCs) adopted by the European Commission',
        'Adequacy decisions for countries recognized as providing adequate data protection',
        'Binding corporate rules where applicable',
        'Supplementary measures for high-risk transfers',
      ]} />
    </Section>

    <Section title="9. Data Processing by AI Services">
      <Para>When you use AI-powered features, your content and prompts are sent to third-party AI providers (OpenAI, Anthropic, Google). These providers act as data processors under your instructions. We ensure:</Para>
      <List items={[
        'Data processing agreements (DPAs) are in place with all AI providers',
        'Providers do not use your data to train their models without your explicit consent',
        'Data is processed in compliance with GDPR requirements',
        'You are informed of which providers process your data and for what purposes',
      ]} />
    </Section>

    <Section title="10. Data Protection Officer">
      <Para>We have appointed a Data Protection Officer (DPO) who can be contacted at dpo@aurelianmarketingengine.com for all matters related to data protection and GDPR compliance.</Para>
    </Section>

    <Section title="11. Right to Lodge a Complaint">
      <Para>If you believe we have not complied with our GDPR obligations, you have the right to lodge a complaint with your supervisory authority. You can also contact us first at privacy@aurelianmarketingengine.com, and we will work to resolve your concern.</Para>
    </Section>

    <Section title="12. Records of Processing Activities">
      <Para>We maintain detailed records of our data processing activities in accordance with Article 30 of the GDPR. These records include the purposes of processing, categories of data subjects and personal data, recipient categories, and data retention schedules.</Para>
    </Section>

    <Section title="13. Data Breach Notification">
      <Para>In the event of a personal data breach, we will:</Para>
      <List items={[
        'Notify the relevant supervisory authority within 72 hours of becoming aware of the breach',
        'Inform affected data subjects without undue delay if the breach is likely to result in high risk',
        'Document all breaches, their effects, and remedial actions taken',
      ]} />
    </Section>

    <Section title="14. Privacy by Design">
      <Para>We follow the principle of privacy by design (Article 25), implementing data protection measures from the earliest stage of development. The Platform architecture enforces data isolation through row-level security, ensuring users can never access another user data by design, not just by policy.</Para>
    </Section>

    <Section title="15. Contact">
      <Para>For GDPR-related questions or to exercise your rights, contact:</Para>
      <Para>Data Protection Officer: dpo@aurelianmarketingengine.com</Para>
      <Para>Privacy Team: privacy@aurelianmarketingengine.com</Para>
    </Section>
  </div>
);

// === Affiliate Terms ===
const AffiliateTerms: React.FC<{ updated: string }> = ({ updated }) => (
  <div>
    <UpdatedBadge date={updated} />
    <h1 className="text-2xl font-bold text-slate-800 mb-4">Affiliate Terms</h1>

    <Section title="1. Who Can Join">
      <Para>The affiliate program is open to anyone, including people who are not Aurelian Marketing Engine customers. Applications are reviewed and approved by us before an affiliate can access links, materials, or payouts.</Para>
    </Section>

    <Section title="2. Attribution and Cookie Window">
      <Para>A referral is credited to the affiliate whose link a visitor used most recently, for 90 days from that visit. The window covers signup, free-to-paid upgrades, and every renewal of the referred subscription that follows.</Para>
      <List items={[
        'Self-referrals are not paid. Signing up or upgrading through your own link earns no commission',
        'Where a visitor arrives through more than one affiliate link, the most recent link is the one credited',
      ]} />
    </Section>

    <Section title="3. Commission Rates">
      <Para>Commission is a percentage of the referred customer subscription fee, paid on each successful billing cycle while the referred customer remains an active paying subscriber. All amounts are in Australian dollars (AUD).</Para>
      <List items={[
        'Starter: 20% for the first 12 months, then 10%',
        'Pro: 25% for the first 12 months, then 12.5%',
        'Agency and Enterprise: 30% for the first 12 months, then 15%',
      ]} />
    </Section>

    <Section title="4. Payouts">
      <List items={[
        'Payouts are paid by Stripe to the payout method you choose on your application',
        'The minimum payout balance is A$50',
        'Payouts are processed monthly once your balance reaches the minimum',
        'Commission from each billing cycle is held for 30 days before it becomes available for payout, so refunds and chargebacks can be reversed first',
      ]} />
    </Section>

    <Section title="5. Refunds, Chargebacks and Clawback">
      <Para>If a referred payment is refunded, charged back, or found to be fraudulent, the commission on that payment is reversed. If the reversal is larger than your available balance, the difference is deducted from future commissions. We may withhold payouts while a reversal or fraud review is open.</Para>
    </Section>

    <Section title="6. Promotion Rules">
      <List items={[
        'Promote Aurelian honestly. Do not make earnings claims, guarantees, or statements about the product that are not true',
        'Do not bid on Aurelian or Aurelian Marketing Engine as a keyword in Google Ads, Bing Ads, or any other paid search or social ad platform, and do not use those terms in ad copy or domain names',
        'Do not send unsolicited bulk messages, spam, or deceptive email. Follow the anti-spam law that applies where you and your audience are located',
        'Use only the marketing materials we provide or approve, and keep your disclosure of any affiliate relationship clear to your audience',
      ]} />
    </Section>

    <Section title="7. Tax Information">
      <List items={[
        'Australian affiliates must provide an ABN before payouts are released',
        'Affiliates outside Australia must provide a completed W-8BEN or W-8BEN-E form before payouts are released',
        'You are responsible for any tax owed on commission you receive',
      ]} />
    </Section>

    <Section title="8. Changes and Termination">
      <Para>We may change commission rates, payout thresholds, or these terms with at least 30 days notice to existing affiliates. We may suspend or end an affiliate account for breaking these terms, for fraudulent referral activity, or for conduct that harms the program.</Para>
    </Section>

    <Section title="9. Contact">
      <Para>Questions about the affiliate program can be sent to legal@aurelianmarketingengine.com.</Para>
    </Section>
  </div>
);

export default LegalHub;
